/**
 * EmbedBody — render a Markdown body INSIDE another page.
 *
 * WHY A SEPARATE, SMALLER RENDERER
 * --------------------------------
 * The article page (`ArticleDetail`) and the lecture page
 * (`LectureDetailPage`) each render their own body, with page-specific
 * extras: the article unwraps template placeholders and renders video
 * fences; the lecture splits its body into typed blocks. An embedded body is
 * neither — it is a block of content dropped into a page that is somebody
 * else's, and it must not carry the host page's chrome.
 *
 * So this renderer handles the SUBSET that makes sense inline: headings,
 * paragraphs, lists, block quotes and images. It reuses the shared inline
 * renderer (`renderInlineMarkdownWithShortcodes`), which means an embedded
 * body can itself contain `{{shortcode}}` — an embed inside an embed, one
 * level of indirection, resolved by the same component.
 *
 * A `## ` heading inside an embed is rendered one step down from the host
 * page's own headings: the host page decides the scale, the embed stays
 * inside it.
 */
import { renderInlineMarkdownWithShortcodes } from '@/utils/inlineMarkdown'
import ShortcodeBlock from './ShortcodeBlock'

interface Props {
  /** The Markdown body. */
  md: string
  className?: string
}

export default function EmbedBody({ md, className }: Props) {
  if (!md || !md.trim()) return null

  const blocks = md.trim().split(/\n{2,}/)

  return (
    <div className={className ?? 'space-y-4'}>
      {blocks.map((raw, i) => {
        const text = raw.trim()
        if (!text) return null

        // Headings and paragraphs may only hold phrasing content, so a
        // `{{shortcode}}` embed inside one is lifted OUT into a sibling
        // block — ShortcodeBlock does that split.
        if (text.startsWith('### ')) {
          return (
            <ShortcodeBlock key={i} as="h4" className="text-base font-bold text-ink mt-6 mb-2" text={text.slice(4)} />
          )
        }
        if (text.startsWith('## ')) {
          return (
            <ShortcodeBlock key={i} as="h3" className="text-lg font-bold text-ink mt-6 mb-2" text={text.slice(3)} />
          )
        }
        if (text.startsWith('# ')) {
          return (
            <ShortcodeBlock key={i} as="h3" className="text-lg font-bold text-ink mt-6 mb-2" text={text.slice(2)} />
          )
        }
        if (text.startsWith('> ')) {
          return (
            <blockquote
              key={i}
              className="border-l-4 border-primary pl-4 my-4 italic text-ink-soft"
            >
              {renderInlineMarkdownWithShortcodes(text.slice(2))}
            </blockquote>
          )
        }
        if (/^[-*]\s+/.test(text) || /^\d+\.\s+/.test(text)) {
          const ordered = /^\d+\.\s+/.test(text)
          const Tag = ordered ? 'ol' : 'ul'
          const items = text.split(/\n/).filter((l) => /^([-*]|\d+\.)\s+/.test(l))
          return (
            <Tag key={i} className="list-disc pl-6 my-4 space-y-1 text-ink-soft">
              {items.map((line, ii) => (
                <li key={ii}>
                  {renderInlineMarkdownWithShortcodes(
                    line.replace(/^([-*]|\d+\.)\s+/, ''),
                  )}
                </li>
              ))}
            </Tag>
          )
        }
        return (
          <ShortcodeBlock key={i} as="p" className="leading-relaxed text-ink-soft" text={text} />
        )
      })}
    </div>
  )
}
